import React from 'react';
import { Card, Button, Space, Tag, Typography, Divider } from 'antd';
import { GlobalOutlined, ShoppingOutlined, CompassOutlined } from '@ant-design/icons';
import { trackEvent } from '../../services/analytics';
import type { Brand } from '../../services/api';
import { useTranslation } from 'react-i18next';

const { Title, Paragraph, Text } = Typography;

interface Props {
  brand: Brand;
}

function hasHard(brand: Brand): boolean {
  const cats = (brand.categories || []).map((x) => x.toLowerCase());
  return (
    cats.includes('watches') ||
    cats.includes('watch') ||
    cats.includes('jewelry') ||
    cats.includes('jewellery')
  );
}

const CHANNELS = {
  overseas: [
    { name: 'Farfetch', url: 'https://www.farfetch.com', icon: <ShoppingOutlined /> },
    { name: 'SSENSE', url: 'https://www.ssense.com', icon: <ShoppingOutlined /> },
    { name: 'Mytheresa', url: 'https://www.mytheresa.com', icon: <ShoppingOutlined /> },
    { name: 'Net‑A‑Porter', url: 'https://www.net-a-porter.com', icon: <ShoppingOutlined /> },
    { name: 'Mr Porter', url: 'https://www.mrporter.com', icon: <ShoppingOutlined /> },
  ],
  watches: [
    { name: 'Chrono24', url: 'https://www.chrono24.com', icon: <CompassOutlined /> },
    { name: 'Watches of Switzerland', url: 'https://www.watches-of-switzerland.co.uk', icon: <CompassOutlined /> },
    { name: 'Bucherer', url: 'https://www.bucherer.com', icon: <CompassOutlined /> },
  ],
  china: [
    { name: '天猫奢品', url: 'https://luxury.tmall.com', icon: <ShoppingOutlined /> },
    { name: '京东奢品', url: 'https://www.jd.com', icon: <ShoppingOutlined /> },
    { name: 'Farfetch 中国', url: 'https://www.farfetch.cn', icon: <ShoppingOutlined /> },
  ],
};

const PurchaseEntryCard: React.FC<Props> = ({ brand }) => {
  const { t } = useTranslation();
  const onClick = (channel: string, url: string) => {
    trackEvent('purchase_link_click', {
      brandId: brand.id,
      brandName: brand.name,
      channel,
      url,
      categories: brand.categories,
      parent_group: brand.parent_group || null,
    });
    window.open(url, '_blank', 'noopener');
  };

  const isHard = hasHard(brand);

  return (
    <Card style={{ marginTop: 24 }}>
      <Title level={3} style={{ marginBottom: 8 }}>{t('purchase_entry.title')}</Title>
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        {t('purchase_entry.description')}
      </Paragraph>

      <div style={{ marginBottom: 12 }}>
        <Space wrap>
          {brand.parent_group && (
            <Tag color="geekblue">{t('purchase_entry.group')}：{brand.parent_group}</Tag>
          )}
          {brand.region_headquarters && (
            <Tag color="blue">{t('purchase_entry.headquarters')}：{brand.region_headquarters}</Tag>
          )}
          {isHard ? (
            <Tag color="volcano">{t('purchase_entry.category_hard')}</Tag>
          ) : (
            <Tag color="green">{t('purchase_entry.category_soft')}</Tag>
          )}
        </Space>
      </div>

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {brand.website && (
          <Button
            icon={<GlobalOutlined />}
            type="primary"
            onClick={() => onClick('Official Site', brand.website)}
            block
            style={{ marginTop: 8, marginBottom: 12 }}
          >
            {t('purchase_entry.official_site')}
          </Button>
        )}

        <div style={{ marginTop: 8 }}>
          <Title level={5} style={{ margin: 0 }}>{t('purchase_entry.overseas')}</Title>
          <Space wrap size={[8, 12]} style={{ marginTop: 8 }}>
            {CHANNELS.overseas.map((c) => (
              <Button key={c.name} icon={c.icon} onClick={() => onClick(c.name, c.url)}>
                {c.name}
              </Button>
            ))}
          </Space>
        </div>

        {isHard && (
          <div style={{ marginTop: 8 }}>
            <Title level={5} style={{ margin: 0 }}>{t('purchase_entry.watches_section')}</Title>
            <Space wrap size={[8, 12]} style={{ marginTop: 8 }}>
              {CHANNELS.watches.map((c) => (
                <Button key={c.name} icon={c.icon} onClick={() => onClick(c.name, c.url)}>
                  {c.name}
                </Button>
              ))}
            </Space>
          </div>
        )}

        <div style={{ marginTop: 8 }}>
          <Title level={5} style={{ margin: 0 }}>{t('purchase_entry.china')}</Title>
          <Space wrap size={[8, 12]} style={{ marginTop: 8 }}>
            {CHANNELS.china.map((c) => (
              <Button key={c.name} icon={c.icon} onClick={() => onClick(c.name, c.url)}>
                {c.name}
              </Button>
            ))}
          </Space>
        </div>
      </Space>
    </Card>
  );
};

export default PurchaseEntryCard;
